/**
 * DriverFuelHistory - List of driver's fuel log records + wizard toggle
 */
import React, { useState, useEffect } from 'react';
import { Fuel, Calendar, AlertCircle, Plus } from 'lucide-react';
import { driverMonoAPI } from '../../../api/missions.js';
import DriverFuelWizard from './DriverFuelWizard.jsx';
import { useFormat } from '../../../i18n/format.js';
import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';

export default function DriverFuelHistory() {
  const { fmt } = useFormat();
  const { money } = useOrgCurrency();
  const [fuelLogs, setFuelLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    loadFuelLogs();
  }, []);

  const loadFuelLogs = async () => {
    try {
      setLoading(true);
      const res = await driverMonoAPI.getFuelLogs({ _limit: 50 });
      setFuelLogs(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (showWizard) {
    return (
      <DriverFuelWizard
        onSuccess={() => { setShowWizard(false); loadFuelLogs(); }}
        onCancel={() => setShowWizard(false)}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
        <p className="text-red-700 dark:text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <button
        onClick={() => setShowWizard(true)}
        className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-blue-700 transition-colors"
      >
        <Plus className="h-4 w-4" />
        Nouveau plein
      </button>

      {fuelLogs.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-8 text-center">
          <Fuel className="h-12 w-12 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-slate-400">Aucun plein de carburant enregistre</p>
        </div>
      ) : (
        fuelLogs.map(log => (
          <div
            key={log.id}
            className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-4"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Fuel className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                <span className="font-medium text-gray-900 dark:text-white">
                  {log.liters ? `${parseFloat(log.liters).toFixed(1)} L` : '\u2014'}
                </span>
                {log.fuel_type && (
                  <span className="px-2 py-0.5 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 text-xs rounded">
                    {log.fuel_type}
                  </span>
                )}
              </div>
              <span className="font-semibold text-gray-900 dark:text-white">
                {log.total_cost
                  ? money(log.total_cost, log)
                  : '\u2014'}
              </span>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-slate-400">
              {log.fuel_date && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {fmt.date(log.fuel_date, {
                    day: '2-digit', month: 'short', year: 'numeric'
                  })}
                </span>
              )}
              {log.vendor && (
                <span className="truncate">Station: {log.vendor}</span>
              )}
              {log.odometer && (
                <span>{fmt.number(parseInt(log.odometer))} km</span>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
